import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle2, XCircle, AlertTriangle, Loader2, Building2 } from "lucide-react";

export default function LandlordMaintenanceAuth() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const initialAction = searchParams.get("action"); // "approve" or "reject"

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [decision, setDecision] = useState(initialAction === "reject" ? "rejected" : initialAction === "approve" ? "approved" : null);
  const [notes, setNotes] = useState("");
  const [done, setDone] = useState(null); // "approved" | "rejected"

  useEffect(() => {
    fetchInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchInfo = async () => {
    try {
      const { data } = await axios.get(`${API}/maintenance/landlord-auth/${token}`);
      setInfo(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Invalid or expired authorization link");
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (!decision) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/maintenance/landlord-auth/${token}`, {
        response: decision,
        notes: notes || null,
      });
      setDone(decision);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to submit response");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (n) =>
    `K ${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full" data-testid="auth-error">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-xl font-semibold mb-2">Link Not Valid</h2>
            <p className="text-muted-foreground">{error}</p>
            <p className="text-sm text-muted-foreground mt-4">
              Please contact the property management office for assistance.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already responded — show status
  if (info?.already_responded || done) {
    const finalDecision = done || info.landlord_response;
    const isApproved = finalDecision === "approved";
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full" data-testid="auth-already-done">
          <CardContent className="pt-6 text-center">
            {isApproved ? (
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            ) : (
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            )}
            <h2 className="text-2xl font-semibold mb-2">
              {isApproved ? "Approved" : "Rejected"}
            </h2>
            <p className="text-muted-foreground">
              {done
                ? `Thank you. Your decision has been recorded and the team has been notified.`
                : `This request was already ${finalDecision}${
                    info.landlord_responded_at
                      ? ` on ${new Date(info.landlord_responded_at).toLocaleDateString()}`
                      : ""
                  }.`}
            </p>
            <div className="mt-6 p-3 bg-gray-50 rounded text-left text-sm">
              <p>
                <strong>Property:</strong> {info.property_title}
              </p>
              <p>
                <strong>Issue:</strong> {info.title}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <Building2 className="w-10 h-10 text-primary mx-auto mb-2" />
          <h1 className="text-2xl font-bold">Maintenance Authorization</h1>
          <p className="text-muted-foreground">
            Hello {info.landlord_name || "Landlord"}, please review the request below.
          </p>
        </div>

        {/* Details */}
        <Card data-testid="auth-details">
          <CardHeader>
            <CardTitle>Request Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Row label="Property" value={`${info.property_title || "N/A"}${info.property_ref ? ` (${info.property_ref})` : ""}`} />
            <Row label="Tenant" value={info.tenant_name} />
            <Row label="Category" value={(info.category || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} />
            <Row label="Priority" value={(info.priority || "").toUpperCase()} />
            <Row label="Issue" value={info.title} />
            <div>
              <p className="text-sm text-muted-foreground mb-1">Description</p>
              <p className="text-sm bg-gray-50 p-3 rounded">{info.description}</p>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-3 border-t">
              <div>
                <p className="text-sm text-muted-foreground">Estimated Cost</p>
                <p className="text-lg font-bold text-red-600">{formatCurrency(info.estimated_cost)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Monthly Rent</p>
                <p className="text-lg font-bold">{formatCurrency(info.monthly_rent)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Decision */}
        <Card className="mt-4">
          <CardHeader>
            <CardTitle>Your Decision</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                size="lg"
                variant={decision === "approved" ? "default" : "outline"}
                onClick={() => setDecision("approved")}
                className={decision === "approved" ? "bg-green-600 hover:bg-green-700" : ""}
                data-testid="approve-btn"
              >
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Approve
              </Button>
              <Button
                type="button"
                size="lg"
                variant={decision === "rejected" ? "default" : "outline"}
                onClick={() => setDecision("rejected")}
                className={decision === "rejected" ? "bg-red-600 hover:bg-red-700" : ""}
                data-testid="reject-btn"
              >
                <XCircle className="w-5 h-5 mr-2" />
                Reject
              </Button>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Notes (optional)</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any conditions, comments, or instructions..."
                rows={3}
                data-testid="notes-input"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600" data-testid="auth-error-msg">{error}</p>
            )}

            <Button
              onClick={submit}
              disabled={!decision || submitting}
              className="w-full"
              size="lg"
              data-testid="submit-decision-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...
                </>
              ) : decision ? (
                `Confirm ${decision === "approved" ? "Approval" : "Rejection"}`
              ) : (
                "Choose Approve or Reject"
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              By confirming, your decision will be recorded and the property management team will be notified.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-start gap-4">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right">{value || "—"}</span>
    </div>
  );
}
